import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, BarChart3 } from "lucide-react";
import api from "../../services/api";

const FALLBACK_DATA = [
  { month: "Out", sales: 0, commissions: 0 },
  { month: "Nov", sales: 0, commissions: 0 },
  { month: "Dez", sales: 0, commissions: 0 },
  { month: "Jan", sales: 0, commissions: 0 },
  { month: "Fev", sales: 0, commissions: 0 },
  { month: "Mar", sales: 0, commissions: 0 },
];

function addGrowth(data) {
  return data.map((item, idx) => {
    if (idx === 0) return { ...item, salesGrowth: null, commGrowth: null };
    const prevSales = data[idx - 1].sales;
    const prevComm = data[idx - 1].commissions;
    return {
      ...item,
      salesGrowth: prevSales > 0 ? Math.round(((item.sales - prevSales) / prevSales) * 100) : (item.sales > 0 ? 100 : 0),
      commGrowth: prevComm > 0 ? Math.round(((item.commissions - prevComm) / prevComm) * 100) : (item.commissions > 0 ? 100 : 0),
    };
  });
}

const GrowthBadge = ({ value }) => {
  if (value === null || value === undefined) return null;
  const isPositive = value > 0;
  const isZero = value === 0;
  const Icon = isPositive ? TrendingUp : isZero ? Minus : TrendingDown;
  const color = isPositive ? "text-emerald-600 bg-emerald-50" : isZero ? "text-gray-500 bg-gray-100" : "text-red-500 bg-red-50";

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {isPositive ? "+" : ""}{value}%
    </span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
      <p className="text-sm font-semibold text-[#0A2F3D] mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-gray-500">Vendas</span>
          <span className="text-sm font-medium text-[#0A2F3D]">{data.sales}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-gray-500">Comissões</span>
          <span className="text-sm font-medium text-emerald-600">
            R$ {(data.commissions / 1000).toFixed(1)}k
          </span>
        </div>
      </div>
    </div>
  );
};

export default function MonthlyChart() {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMonthly() {
      try {
        const res = await api.get("/dashboard/monthly");
        const months = res.data.months || [];
        setRawData(
          months.map((m) => ({
            month: m.month,
            sales: m.sales,
            commissions: m.commissions,
            salesGrowth: m.sales_growth,
            commGrowth: m.comm_growth,
          }))
        );
      } catch {
        setRawData(null);
      } finally {
        setLoading(false);
      }
    }
    fetchMonthly();
  }, []);

  const chartData = rawData || addGrowth(FALLBACK_DATA);
  const data = rawData ? chartData : addGrowth(FALLBACK_DATA);
  const hasData = data.some((d) => d.sales > 0 || d.commissions > 0);

  const currentMonth = data[data.length - 1];
  const prevMonth = data[data.length - 2];

  const BAR_COLORS = data.map((_, idx) => idx === data.length - 1 ? "#3B82F6" : "#94a3b8");

  if (loading) {
    return (
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="monthly-chart">
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="monthly-chart">
      <div className="flex items-start justify-between mb-4 lg:mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-[#3B82F6]" />
            <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Resultado Mensal</h3>
          </div>
          <p className="text-xs lg:text-sm text-gray-500">Vendas fechadas - Últimos 6 meses</p>
        </div>

        <div className="text-right">
          <div className="flex items-center gap-2 justify-end">
            <p className="text-2xl font-bold text-[#0A2F3D]">{currentMonth?.sales || 0}</p>
            {currentMonth?.salesGrowth != null && <GrowthBadge value={currentMonth.salesGrowth} />}
          </div>
          <p className="text-xs text-gray-500">{currentMonth?.month} vs {prevMonth?.month}</p>
        </div>
      </div>

      <div className="h-48 lg:h-56">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} width={30} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(59, 130, 246, 0.05)" }} />
              <Bar dataKey="sales" radius={[6, 6, 0, 0]} maxBarSize={40}>
                {data.map((_, idx) => (
                  <Cell key={idx} fill={BAR_COLORS[idx]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            Nenhuma venda registrada ainda. Os dados aparecerão aqui conforme leads forem fechados.
          </div>
        )}
      </div>

      {/* Monthly breakdown */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {data.map((item, idx) => (
            <div
              key={item.month}
              className={`text-center p-2 rounded-lg ${
                idx === data.length - 1 ? "bg-[#3B82F6]/5 ring-1 ring-[#3B82F6]/20" : "bg-gray-50"
              }`}
            >
              <p className="text-xs text-gray-400 mb-0.5">{item.month}</p>
              <p className={`text-sm font-semibold ${idx === data.length - 1 ? "text-[#3B82F6]" : "text-[#0A2F3D]"}`}>
                {item.sales}
              </p>
              {item.salesGrowth != null && <GrowthBadge value={item.salesGrowth} />}
            </div>
          ))}
        </div>
      </div>

      {/* Commission total */}
      <div className="mt-3 p-3 bg-emerald-50/50 rounded-xl flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">Comissões acumuladas (6 meses)</span>
        <span className="text-sm font-bold text-emerald-600">
          R$ {(data.reduce((sum, d) => sum + (d.commissions || 0), 0) / 1000).toFixed(1)}k
        </span>
      </div>
    </div>
  );
}
